import type { PowerAnalysisResult, ExperimentRecommendation } from 'statisticsdb/types';
import type { Experiment } from 'types';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'hooks/useQueryState';
import { useClient } from 'hooks/useClient';
import { resolveReportCurrency } from 'helpers/currency';
import { readFrequentistVariants } from 'helpers/significance-payload';
import { resolveObjectives } from 'helpers/objectives';
import { config } from 'config';

const GOALS = ['Conversions', 'Revenue'] as const;
const EVALUATION_METHODS = ['frequentist', 'bayesian'] as const;
const CORRECTION_METHODS = ['holm-bonferroni', 'benjamini-hochberg', 'sidak', 'none'] as const;

type EvaluationMethod = (typeof EVALUATION_METHODS)[number];
type CorrectionMethod = (typeof CORRECTION_METHODS)[number];

type ConfidenceInterval = {
  lower: number;
  upper: number;
};

type VariantStats = {
  average: number;
  revenue: number;
  sessions: number;
  difference: number | null;
  isOriginal: boolean;
  conversions: number;
  improvement: number | null;
  revenueAverage: number;
  revenueDifference: number | null;
  revenueImprovement: number | null;
};

type FrequentistSignificanceStats = {
  pValue: number;
  zScore: number;
  sprtResult: string;
  significant: boolean;
  variantName: string;
  confidenceInterval?: ConfidenceInterval;
  liftInterval?: ConfidenceInterval;
  warning?: string;
};

type BayesianSignificanceStats = {
  variantName: string;
  probabilityOfBeingBest: number;
  expectedLoss: number;
  credibleInterval: ConfidenceInterval;
  liftInterval: ConfidenceInterval;
};

export type SignificanceDisplayItem = {
  id: number;
  name: string;
  method: EvaluationMethod;
  pValue?: number;
  zScore?: number;
  sprtResult?: string;
  significant?: boolean;
  probabilityOfBeingBest?: number;
  expectedLoss?: number;
  credibleInterval?: ConfidenceInterval;
  confidenceInterval?: ConfidenceInterval;
  liftInterval?: ConfidenceInterval;
  warning?: string;
};

/**
 * One variant's reading of one secondary objective.
 *
 * No p-value and no `significant`: secondaries are supporting evidence, judged
 * outside the primary's correction family, and a verdict here would invite
 * reading a losing experiment as a win on whichever secondary happened to move.
 */
type SecondaryVariantStats = {
  variantName: string;
  sessions: number;
  conversions: number;
  /** Percentage, already rounded by the stored procedure. */
  rate: number;
  rateInterval: ConfidenceInterval;
  isOriginal: boolean;
  /** Percentage against control; null on the control row itself. */
  lift: number | null;
  /** Null when the interval is undefined, e.g. a control with no conversions. */
  liftInterval: ConfidenceInterval | null;
};

type ChartDataPoint = {
  date: string;
  value: number;
};

type VariantChartData = {
  data: ChartDataPoint[];
  variantName: string;
};

// Imported, not redeclared: the local copy described four fields where the payload
// carries nine, so anything the server added was invisible to the dashboard's types.
type ExperimentLevelStatsEntry = {
  power: PowerAnalysisResult;
  recommendation: ExperimentRecommendation;
};

type ExperimentStatsResponse = {
  id: number;
  experimentId: number;
  experimentName: string;
  websiteId: number;
  controlVariantId: number | null;
  statisticsData: Record<string, Record<string, VariantStats>>;
  /**
   * Either shape. Rows written now nest by correction; rows written before this branch
   * keyed variant ids directly under the goal. readFrequentistVariants accepts both,
   * so a production row reports its real numbers instead of an empty section.
   */
  significanceData: Record<
    string,
    Record<string, { frequentist?: Record<string, Record<string, FrequentistSignificanceStats>> }>
  >;
  bayesianData: Record<string, Record<string, { bayesian: Record<string, BayesianSignificanceStats> }>>;
  chartData: Record<string, Record<string, Record<string, VariantChartData>>>;
  experimentLevelStats: Record<string, Record<string, ExperimentLevelStatsEntry>>;
  /** { currency: { eventName: { variantId: SecondaryVariantStats } } } */
  secondaryStats?: Record<string, Record<string, Record<string, SecondaryVariantStats>>>;
  updatedAt: string;
};

export const useExperimentStats = (experiment: Experiment) => {
  const [currency, setCurrency] = useQueryState<string | null>('currency', null);
  const [goal, setGoal] = useQueryState<string>('goal', GOALS[0]);
  const [evaluationMethod, setEvaluationMethod] = useQueryState<EvaluationMethod>('method', 'frequentist');
  const [correctionMethod, setCorrectionMethod] = useQueryState<CorrectionMethod>('correction', 'holm-bonferroni');
  const [hasBeenSet, setHasBeenSet] = useState(false);
  const statisticsClient = useClient(config.STATISTICS_URL);
  const isRunning = experiment?.status === 'Running';
  const isDeployed = experiment?.status === 'Deployed';
  const { variants } = experiment;

  const { data: experimentStats, isLoading } = useQuery<ExperimentStatsResponse | null>({
    queryKey: ['EXPERIMENT_STATS', experiment.id],
    queryFn: () => statisticsClient.get(`/${experiment.id}/experiment-stats`),
    enabled: !!experiment,
    refetchInterval: isRunning || isDeployed ? 1000 * 60 : false,
  });

  const currencies = experimentStats?.statisticsData ? Object.keys(experimentStats.statisticsData) : [];

  const statistics =
    currency && experimentStats?.statisticsData?.[currency] ? experimentStats.statisticsData[currency] : null;

  const significance = buildSignificanceDisplay({
    stats: experimentStats,
    currency,
    goal,
    evaluationMethod,
    correctionMethod,
  });

  const chartDataRaw =
    currency && goal && experimentStats?.chartData?.[currency]?.[goal]
      ? experimentStats.chartData[currency][goal]
      : null;

  const totalSessions = statistics
    ? Object.values(statistics).reduce((acc: number, stat) => acc + (stat.sessions || 0), 0)
    : 0;

  const original = variants?.find((v: any) => v.name === 'Original');

  const levelStats =
    currency && goal && experimentStats?.experimentLevelStats?.[currency]?.[goal]
      ? experimentStats.experimentLevelStats[currency][goal]
      : null;

  const power = levelStats?.power ?? null;
  const recommendation = levelStats?.recommendation ?? null;

  useEffect(() => {
    if (currencies.length > 0 && !hasBeenSet) {
      setHasBeenSet(true);
      // Default to all currencies. Every figure is now currency-scoped, so opening
      // on a single currency showed one slice of the experiment as if it were the
      // whole thing: experiment 5364 defaulted to AUD, which holds 1 of its 6195
      // conversions, and reported no effect for a variant that is 4.25% ahead
      // overall.
      //
      // The aggregate bucket now always exists for a multi-currency experiment. It did
      // not before, so this fell through to currencies[0] for 259 of them and the
      // default was whichever code sorted first -- AED, for experiment 3948, holding
      // 41 of its 5897 conversions.
      const resolved = resolveReportCurrency(currencies);
      if (resolved) setCurrency(resolved);
    }
  }, [currencies, hasBeenSet]);

  const { primary, secondaries, primaryIsDefault } = resolveObjectives(experiment);

  // Generic when nothing is mapped: the pipeline measures 'purchase' either way,
  // but naming it here would imply a configuration the website does not have.
  const goalLabels: Record<string, string> = primaryIsDefault
    ? { Conversions: 'Conversions', Revenue: 'Revenue' }
    : { Conversions: primary.charAt(0).toUpperCase() + primary.slice(1), Revenue: 'Revenue' };

  const secondaryStats = currency ? (experimentStats?.secondaryStats?.[currency] ?? null) : null;

  return {
    GOALS,
    EVALUATION_METHODS,
    CORRECTION_METHODS,
    goalLabels,
    statistics,
    significance,
    chartDataRaw,
    variants,
    original,
    totalSessions,
    currencies,
    currency,
    goal,
    evaluationMethod,
    correctionMethod,
    power,
    recommendation,
    primaryObjective: primary,
    secondaryObjectives: secondaries,
    secondaryStats,
    isRunning,
    isLoading: isLoading || !experimentStats,
    setGoal,
    setCurrency,
    setEvaluationMethod,
    setCorrectionMethod,
  };
};

type BuildSignificanceDisplayParams = {
  stats: ExperimentStatsResponse | null | undefined;
  currency: string | null;
  goal: string;
  evaluationMethod: EvaluationMethod;
  correctionMethod: CorrectionMethod;
};

function buildSignificanceDisplay({
  stats,
  currency,
  goal,
  evaluationMethod,
  correctionMethod,
}: BuildSignificanceDisplayParams): SignificanceDisplayItem[] | null {
  if (!stats || !currency) return null;

  if (evaluationMethod === 'bayesian') {
    const bayesianGoalData = stats.bayesianData?.[currency]?.[goal]?.bayesian;
    if (!bayesianGoalData) return null;

    return Object.entries(bayesianGoalData).map(([variantId, data]) => ({
      id: +variantId,
      name: data.variantName ?? `Variant ${variantId}`,
      method: 'bayesian' as const,
      probabilityOfBeingBest: data.probabilityOfBeingBest,
      expectedLoss: data.expectedLoss,
      credibleInterval: data.credibleInterval,
      liftInterval: data.liftInterval,
    }));
  }

  // Accepts the pre-correction shape too, so a row written before this branch reports
  // its real numbers instead of an empty section.
  const freqGoalData = readFrequentistVariants<FrequentistSignificanceStats>(
    stats.significanceData?.[currency]?.[goal],
    correctionMethod,
  );
  if (!freqGoalData) return null;

  return Object.entries(freqGoalData).map(([variantId, data]) => ({
    id: +variantId,
    name: data.variantName ?? `Variant ${variantId}`,
    method: 'frequentist' as const,
    pValue: data.pValue,
    zScore: data.zScore,
    sprtResult: data.sprtResult,
    significant: data.significant,
    confidenceInterval: data.confidenceInterval,
    liftInterval: data.liftInterval,
    warning: data.warning,
  }));
}
